import React from 'react';
import { cx } from '@/styled-system/css';
import { Button } from '../Button';
import { CloseIcon } from '../icons';
import { floatingCloseButton } from './closeButton.styles';

type CloseButtonVariant = 'standard' | 'floating';

export type CloseButtonProps = {
  onClick: () => void;
  variant?: CloseButtonVariant;
  'aria-label'?: string;
  disabled?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'disabled' | 'aria-label'>;

/**
 * CloseButton - Standalone reusable close button component
 *
 * Extracted from FullscreenOverlay to remove context dependency.
 * Uses HALO Button component with CloseIcon for consistency.
 *
 * @example Standard close button
 * <CloseButton
 *   onClick={handleClose}
 *   aria-label="Close dialog"
 * />
 *
 * @example Floating close button for overlays
 * <CloseButton
 *   variant="floating"
 *   onClick={handleClose}
 *   aria-label="Close overlay"
 * />
 */
export const CloseButton = ({
  onClick,
  variant = 'standard',
  'aria-label': ariaLabel = 'Close',
  disabled = false,
  className,
  ...buttonProps
}: CloseButtonProps) => {
  // Apply floating styles only when floating variant
  const floatingStyles = variant === 'floating' ? floatingCloseButton : '';
  const combinedClassName = cx(floatingStyles, className);

  return (
    <Button
      shape="circle"
      tone="neutral"
      emphasis="outlined"
      size="md"
      onClick={onClick}
      aria-label={ariaLabel}
      disabled={disabled}
      className={combinedClassName}
      {...buttonProps}
    >
      <CloseIcon />
    </Button>
  );
};
